import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Train, Bus, Locate, ArrowRight, ExternalLink as ExternalLinkIcon, MapPin, WifiOff } from "lucide-react";
import ExternalLink from "./ExternalLink";
import { api } from "../lib/api";
import { useOnlineStatus } from "../lib/useOnlineStatus";
import { getOfflineRoute } from "../lib/storage";
import { track } from "../lib/telemetry";

const LAST_COORDS_KEY = "td_last_coords";
const CACHED_COORDS_MAX_AGE_MS = 60 * 60 * 1000; // 1h — a fresh session's coords

const MODE_LABEL = { bus: "Bus", train: "Train", subway: "Tube", tram: "Tram", ferry: "Ferry", rail: "Train", heavy_rail: "Train" };

export default function TransitSection({ locationId, resourcePath = "locations", offlineKey }) {
  const [mode, setMode] = useState("location"); // location | postcode
  const [postcode, setPostcode] = useState("");
  const [trigger, setTrigger] = useState(0);
  const [origin, setOrigin] = useState(() => {
    // Auto-fill from Discover's cached coords so the user doesn't have to
    // re-grant permission or wait for the async geolocation round-trip.
    try {
      const raw = localStorage.getItem(LAST_COORDS_KEY);
      if (!raw) return null;
      const c = JSON.parse(raw);
      if (Date.now() - (c.ts || 0) > CACHED_COORDS_MAX_AGE_MS) return null;
      return { lat: c.lat, lng: c.lng };
    } catch { return null; }
  });
  const online = useOnlineStatus();
  const [savedDeepLink, setSavedDeepLink] = useState(null);
  const idbKey = offlineKey || locationId;

  // Load the saved route's Google Maps deep link when offline (or as fallback).
  // Re-runs when the user saves/removes this route via `td-offline-changed`.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getOfflineRoute(idbKey).then((r) => {
        if (cancelled) return;
        setSavedDeepLink(r ? {
          transit: r.google_transit_deep_link,
          search: r.google_search_deep_link,
          name: r.name,
        } : null);
      }).catch(() => {});
    };
    load();
    window.addEventListener("td-offline-changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("td-offline-changed", load);
    };
  }, [idbKey]);

  useEffect(() => {
    if (mode !== "location") return;
    if (origin) return; // Already have cached coords — skip the prompt.
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setOrigin(c);
        try { localStorage.setItem(LAST_COORDS_KEY, JSON.stringify({ ...c, ts: Date.now() })); } catch { /* ignore */ }
      },
      () => setOrigin(null),
      { timeout: 8000 }
    );
  }, [mode, origin]);

  const goFind = () => {
    // Fire lightweight telemetry when the walker actually plans a route
    // to this destination — conversion-quality signal for the admin dashboard.
    // `track()` is already silent-fail (try/catch + swallowed promise).
    const isBasecamp = resourcePath === "basecamps";
    // Lunch-intent overlay for basecamps: read the session flag set by
    // BasecampsCarousel's "Perfect lunch stop" chip or the Water Tap /
    // Picnic Area amenity chips. Lets the Signals tile split the actual
    // bums-on-bus conversion for lunch-intent walkers vs everyone else.
    let viaLunchFilter = false;
    if (isBasecamp) {
      try { viaLunchFilter = sessionStorage.getItem("td_lunch_intent") === "1"; } catch { /* ignore */ }
    }
    track(
      isBasecamp ? "basecamp_transit_planned" : "transit_requested",
      {
        [isBasecamp ? "basecamp_id" : "location_id"]: locationId,
        origin_mode: mode,
        ...(isBasecamp && viaLunchFilter ? { via_lunch_filter: true } : {}),
      },
    );
    setTrigger((x) => x + 1);
  };

  const transit = useQuery({
    queryKey: ["transit", resourcePath, locationId, mode, postcode, origin, trigger],
    enabled: trigger > 0 && online,
    queryFn: async () => {
      const body = mode === "postcode"
        ? { origin_postcode: postcode }
        : origin ? { origin_lat: origin.lat, origin_lng: origin.lng } : null;
      if (!body) return { status: "NO_ORIGIN" };
      const r = await api.post(`/${resourcePath}/${locationId}/transit`, body);
      return r.data;
    },
  });

  // ─── OFFLINE MODE ───────────────────────────────────────────────
  // Live Google Directions calls are disabled to comply with Google Maps
  // Platform ToS. We instead surface the pre-composed native-app deep link
  // that was baked into IndexedDB when the user saved the route offline.
  if (!online) {
    return (
      <section className="td-card p-5 sm:p-6 mt-5" data-testid="transit-section-offline">
        <div className="flex items-center gap-2">
          <Train size={18} className="text-brand-primary" />
          <h3 className="font-heading text-lg font-medium">Public transport</h3>
          <span
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-accent border border-brand-accent/30"
            data-testid="transit-offline-badge"
          >
            <WifiOff size={10} /> Offline
          </span>
        </div>
        <p className="text-sm text-brand-subtle mt-3 leading-relaxed">
          Live transit schedules are unavailable while you&apos;re offline.
          {savedDeepLink
            ? " Tap below to hand off to Google Maps — it will use your device's current location and live schedules once you tap through."
            : " Save this walk offline first to keep the last-mile Google Maps handoff working without signal."}
        </p>
        {savedDeepLink && (
          <ExternalLink
            testid="transit-offline-deeplink"
            href={savedDeepLink.transit}
            className="td-btn-primary mt-4 w-full !py-2.5 !text-sm justify-center"
          >
            <ExternalLinkIcon size={14} /> Open in Google Maps
          </ExternalLink>
        )}
      </section>
    );
  }

  return (
    <section className="td-card p-5 sm:p-6 mt-5" data-testid="transit-section">
      <div className="flex items-center gap-2">
        <Train size={18} className="text-brand-primary" />
        <h3 className="font-heading text-lg font-medium">Live public transport</h3>
      </div>

      <div className="mt-4 inline-flex bg-brand-sand border border-brand-border rounded-full p-1 text-sm">
        <button
          data-testid="transit-pill-location"
          onClick={() => setMode("location")}
          className={`px-4 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "location" ? "bg-brand-primary text-brand-sand" : "text-brand-subtle"}`}
        >
          <Locate size={13} /> Use my location
        </button>
        <button
          data-testid="transit-pill-postcode"
          onClick={() => setMode("postcode")}
          className={`px-4 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "postcode" ? "bg-brand-primary text-brand-sand" : "text-brand-subtle"}`}
        >
          <MapPin size={13} /> Enter postcode
        </button>
      </div>

      {mode === "postcode" && (
        <div className="flex gap-2 mt-3">
          <input
            data-testid="transit-postcode-input"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            placeholder="e.g. SW1A 1AA"
            className="td-input flex-1"
            onKeyDown={(e) => e.key === "Enter" && goFind()}
          />
          <button data-testid="transit-find-btn" onClick={goFind} className="td-btn-primary !px-5">Find</button>
        </div>
      )}
      {mode === "location" && (
        <button data-testid="transit-find-btn-loc" onClick={goFind} className="td-btn-primary mt-3 w-full">
          Plan from my location
        </button>
      )}

      {transit.isFetching && <div className="mt-4 text-sm text-brand-subtle">Finding the best route…</div>}

      {transit.data && transit.data.status === "POSTCODE_NOT_FOUND" && (
        <div className="mt-4 text-sm text-brand-error">We couldn't find that UK postcode.</div>
      )}
      {transit.data && transit.data.status && transit.data.status !== "OK" && transit.data.status !== "POSTCODE_NOT_FOUND" && transit.data.status !== "NO_ORIGIN" && (
        <div className="mt-4 text-sm text-brand-subtle">No public transport route was found between here and the trailhead.</div>
      )}

      {transit.data?.status === "OK" && (
        <div className="mt-4 space-y-4" data-testid="transit-result">
          <div className="flex items-center justify-between text-sm">
            <div>
              <div className="font-medium text-brand-text">{transit.data.duration} total</div>
              <div className="text-brand-subtle text-xs">
                {transit.data.departure_time} <ArrowRight size={11} className="inline" /> {transit.data.arrival_time}
              </div>
            </div>
            <div className="flex gap-1.5">
              {transit.data.modes.map((m) => (
                <span key={m} className="td-chip capitalize">{MODE_LABEL[m] || m}</span>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {transit.data.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-t border-brand-border/50 first:border-t-0">
                <span
                  className="inline-flex shrink-0 items-center justify-center text-white font-bold text-xs px-2 py-1 rounded-md"
                  style={{ background: s.line_color }}
                >
                  {s.line_name || "—"}
                </span>
                <div className="flex-1 text-sm">
                  <div className="font-medium text-brand-text">
                    {s.departure_stop} <ArrowRight size={11} className="inline" /> {s.arrival_stop}
                  </div>
                  <div className="text-brand-subtle text-xs">
                    {(MODE_LABEL[s.mode] || s.mode)} · {s.headsign && `towards ${s.headsign} · `}{s.num_stops} stop{s.num_stops === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-xs text-brand-subtle whitespace-nowrap">{s.departure_time} → {s.arrival_time}</div>
              </div>
            ))}
          </div>
          <ExternalLink
            testid="transit-google-link"
            href={transit.data.deep_link}
            className="inline-flex items-center gap-1.5 text-sm text-brand-accent font-medium"
          >
            Open in Google Maps for live times <ExternalLinkIcon size={13} />
          </ExternalLink>
        </div>
      )}
    </section>
  );
}
